import {
  resolveStorefrontFeatures,
  resolveStorefrontTerminology,
  SELF_EXCLUSION_DAY_OPTIONS,
  selfExclusionDurationLabel,
  selfExclusionWords,
} from "@loot-os/api-contract";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";

import { SectionSwitch, StorefrontSection } from "../components/StorefrontSection";
import { useCurrency } from "../lib/currency";
import { useStorefrontConfig } from "../lib/storefront";
import { useTenantRole } from "../lib/useTenantRole";

/**
 * Self-exclusion on one storefront environment: the switch (`features.selfExclusion`, saved on
 * flip through `storefront.update` as its one key), then what an exclusion blocks and leaves open
 * on this store, in its own words. Off hides the panel on the Account page and the API refuses a
 * new exclusion; one a shopper already started is enforced until it ends.
 */
export function StorefrontSelfExclusionPage() {
  const { can } = useTenantRole();
  const { query, update } = useStorefrontConfig();
  const currency = useCurrency();

  // Narrowing only: the layout renders a section once the same query has data.
  if (!query.data) return null;

  const { config, environment } = query.data;
  const features = resolveStorefrontFeatures(config.features);
  const words = selfExclusionWords({
    terms: resolveStorefrontTerminology(config.terminology),
    currencyPlural: currency.namePlural,
    features,
  });

  return (
    <StorefrontSection
      title="Self-exclusion"
      description={`Lets a signed-in shopper block their own spending for ${SELF_EXCLUSION_DAY_OPTIONS.map(selfExclusionDurationLabel).join(", ")}, from Account → Settings → Advanced. It can only be made longer, and nobody can end one early, support included. Switching it off hides the option; exclusions already running stay in force until they end.`}
      environment={environment}
      aside={
        <SectionSwitch
          id="self-exclusion-enabled"
          label="Self-exclusion"
          checked={features.selfExclusion}
          disabled={update.isPending || !can("editor")}
          onCheckedChange={(checked) => update.mutate({ features: { selfExclusion: checked } })}
        />
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>What shoppers are told</CardTitle>
          <CardDescription>{words.irreversible}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 sm:grid-cols-2">
          <RuleList title={words.blockedTitle} lines={words.blocked} />
          <RuleList title={words.allowedTitle} lines={words.allowed} />
        </CardContent>
      </Card>
    </StorefrontSection>
  );
}

function RuleList({ title, lines }: { title: string; lines: readonly string[] }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium">{title}</p>
      <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
}
